import { theme } from '@services/theme.service/theme';
import { rem } from '@visiba/std';
import { createContext, ReactNode, useContext, useMemo } from 'react';
import styled from 'styled-components';
import { Badge } from './badge.component';
import { Icon, IconType } from './icon.component';

const elements = {
	container: styled.div<{ size: number }>`
		flex-shrink: 0;
		position: relative;
		width: ${({ size }) => rem(size)};
		height: ${({ size }) => rem(size)};
	`,
	innerContainer: styled.div`
		width: 100%;
		height: 100%;
		border-radius: 50%;
		overflow: hidden;
	`,
	img: styled.img`
		width: 100%;
		height: 100%;
		object-fit: cover;
		object-position: center;
	`,
	icon: styled.div<{ size: number }>`
		width: 100%;
		height: 100%;
		display: flex;
		justify-content: center;
		align-items: center;

		svg {
			color: ${theme.primary};
			font-size: ${({ size }) => rem(size)};
		}
	`,
	badge: styled.div`
		position: absolute;
		top: 0;
		left: 100%;
		transform: translate(-66.66%, -10%);
	`,
};

const DEFAULT_SIZE = 50;

interface AvatarContextProps {
	size: number;
}

const AvatarContext = createContext<AvatarContextProps>({ size: DEFAULT_SIZE });

interface AvatarProps {
	children: ReactNode;
	size?: number;
}

export function Avatar({ children, size }: AvatarProps): JSX.Element {
	const value = useMemo(() => ({ size: size ?? DEFAULT_SIZE }), [size]);

	return (
		<AvatarContext.Provider value={value}>
			<elements.container size={value.size}>{children}</elements.container>
		</AvatarContext.Provider>
	);
}

interface ImageProps {
	src: string;
}

function AvatarImage({ src }: ImageProps): JSX.Element {
	return (
		<elements.innerContainer>
			<elements.img src={src} data-testid='avatar-img' />
		</elements.innerContainer>
	);
}

Avatar.Image = AvatarImage;

interface IconProps {
	type: IconType;
}

function AvatarIcon({ type }: IconProps): JSX.Element {
	const context = useContext(AvatarContext);

	return (
		<elements.innerContainer>
			<elements.icon size={context.size}>
				<Icon type={type} />
			</elements.icon>
		</elements.innerContainer>
	);
}

Avatar.Icon = AvatarIcon;

interface BadgeProps {
	text?: string;
	amount?: number;
}

function AvatarBadge({ text, amount }: BadgeProps): JSX.Element {
	return (
		<elements.badge data-testid='avatar-badge'>
			<Badge>
				{text && <Badge.Text value={text} />}
				{amount && <Badge.Amount value={amount} />}
			</Badge>
		</elements.badge>
	);
}

Avatar.Badge = AvatarBadge;
